//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import angular from 'angular';
import { PojoMap } from 'pojo-maps';
import { PojoSet } from 'pojo-sets';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';

import {
  getRightsAutomationConfigurations,
  sendMediasToRightAutomation,
} from '@account-settings/redux/rights-management/automateRightsRequestingSlice';
import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import { FullscreenModal } from '@components/FullscreenModal';
import { Alert } from '@components/atoms/alert/Alert';
import { PrimaryButton } from '@components/atoms/button/Button';
import { InlineSpinner } from '@components/atoms/spinner/Spinner';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import { requestRightsModalActions } from '@galleries/actions/requestRightsModalActions';
import { requestRightsNonMentionedModalActions } from '@galleries/actions/requestRightsNonMentionedModalActions';
import { rightsSkippedModalActions } from '@galleries/actions/rightsSkippedModalActions';
import { DarkGradientOverlay } from '@galleries/components/content/dark-gradient-overlay/DarkGradientOverlay';
import { InfoOverlay } from '@galleries/components/content/info-overlay/InfoOverlay';
import { MediaGrid } from '@galleries/components/content/media-grid/MediaGrid';
import { PendingIconOverlay } from '@galleries/components/content/pending-icon/PendingIconOverlay';
import RotatingRightsMessages from '@galleries/components/content/rotating-rights-messages/RotatingRightsMessages';
import SelectableImage from '@galleries/components/content/selected-image/SelectableImage';
import { WorkflowBreadcrumbs } from '@galleries/components/content/workflow-breadcrumbs/WorkflowBreadcrumbs';
import { RightsSkippedModal } from '@galleries/components/modals/rights/RightsSkippedModal';
import {
  BULK_RIGHTS_WORKFLOW_LARGE_MEDIA_WIDTH,
  BULK_RIGHTS_WORKFLOW_SMALL_MEDIA_WIDTH,
  GalleriesTrackingIds,
} from '@galleries/constants/galleriesConstants';
import { getIsIncludeUsernameEnabled } from '@galleries/selectors/bulk-rights-selectors/getRightsPersonalizationFeatures';
import { splitMentionedItems } from '@galleries/selectors/splitMentionedItems';
import { getContentItemImageUrl } from '@galleries/utils/getContentItemImageUrl';
import { instagramAccountActions } from '@instagram-account/redux/account/instagramAccountActions';
import { requestRightsActions } from '@legacy-components/rights-management/request-rights-actions';
import { personalizeRightsRequestMessage } from '@legacy-components/rights-management/request-rights-util';
import { ICuralateStore } from '@models/AppState';
import PartialCuid from '@models/PartialCuid';
import makeAngularReactController from '@react-bridge/angular-react-widget';
import { getBulkActionsActions } from '@react-bridge/wrappers/ng-service-provider';
import { Normalized } from '@utils/normalize';

import { requestMultiHandleRightsGraphApi } from 'app/bulk-actions/universal-image-actions-api';
import { ReduxDispatch } from 'app/curalate.store';
import { getHasMultiHandleInstagramEnabled } from '@account-settings/redux/facebook-instagram/selectors/getMultiHandleFeature';

const mapStateToProps = (state: ICuralateStore) => {
  const { atMentionedItems, nonAtMentionedItems } = splitMentionedItems(state);
  const requestRightsModalState = state.galleries.requestRightsModal;

  return {
    isOpen: requestRightsModalState.isOpen,
    selectedItems: requestRightsModalState.selectedItems,
    materializedItems: requestRightsModalState.materializedItems,
    itemsPendingApproval: requestRightsModalState.itemsPendingApproval,
    atMentionedItems,
    nonAtMentionedItems,
    hasChromeExtension: requestRightsModalState.hasChromeExtension,
    session: state.session,
    instagram: state.instagram.account,
    requestRights: state.requestRights,
    hasUIARequestRightsPermission:
      state.session.permissions['universal_image_actions:request_rights'],
    isAutomated:
      state.accountSettings.rightsManagement.automateRightsRequests
        .automationConfigurations.isAutomated,

    // TODO: PD-249308 - remove rights_requests_include_username flag
    isIncludeUsernameEnabled: getIsIncludeUsernameEnabled(state),
    isMultiHandleFeatureEnabled: getHasMultiHandleInstagramEnabled(state),
  };
};

const mapDispatchToProps = (dispatch: ReduxDispatch) => {
  return {
    actions: {
      getRightsAutomationConfigurations: () =>
        dispatch(getRightsAutomationConfigurations()),
      modal: bindActionCreators(requestRightsModalActions, dispatch),
      instagram: bindActionCreators(instagramAccountActions, dispatch),
      requestRights: bindActionCreators(requestRightsActions, dispatch),
      requestRightsNonMentionedModal: bindActionCreators(
        requestRightsNonMentionedModalActions,
        dispatch,
      ),
      bulkActions: bindActionCreators(getBulkActionsActions(), dispatch),
      rightsSkippedModal: bindActionCreators(
        rightsSkippedModalActions,
        dispatch,
      ),
      sendMediasToRightAutomation: (cuids: PartialCuid[]) =>
        dispatch(sendMediasToRightAutomation(cuids)),
    },
  };
};

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

export class RequestRightsAtMentioned extends React.Component<Props> {
  public state = {
    showNoSelectedItemsAlert: false,
  };

  public async componentDidMount() {
    const {
      actions: {
        getRightsAutomationConfigurations,
        instagram: { fetchDefaultInstagramAccount },
        modal: { isChromeExtensionPresent },
        sendMediasToRightAutomation,
      },
      isAutomated,
      instagram: { defaultInstagramAccount },
      hasUIARequestRightsPermission,
      selectedItems,
    } = this.props;

    isChromeExtensionPresent();
    if (!defaultInstagramAccount) {
      await fetchDefaultInstagramAccount();
    }
    if (
      isAutomated &&
      Object.keys(selectedItems).length > 0 &&
      hasUIARequestRightsPermission
    ) {
      const partialCuids = PojoMap.values(selectedItems).map(PartialCuid.build);
      sendMediasToRightAutomation(partialCuids);
    }
    if (isAutomated === null) {
      getRightsAutomationConfigurations();
    }
  }

  public componentDidUpdate(prevProps: Props) {
    const {
      actions: { sendMediasToRightAutomation },
      hasUIARequestRightsPermission,
      isAutomated,
      selectedItems,
    } = this.props;
    if (
      prevProps.isAutomated === null &&
      isAutomated &&
      Object.keys(selectedItems).length > 0 &&
      hasUIARequestRightsPermission
    ) {
      const partialCuids = PojoMap.values(selectedItems).map(PartialCuid.build);
      sendMediasToRightAutomation(partialCuids);
    }
  }
  public render() {
    const {
      isAutomated,
      isOpen,
      instagram,
      selectedItems,
      atMentionedItems,
      nonAtMentionedItems,
      hasChromeExtension,
      hasUIARequestRightsPermission,
      actions: {
        modal: { closeModal },
        rightsSkippedModal: { openRightsSkippedModal },
      },
      requestRights: { isRequestingRights },
    } = this.props;

    const brandName = instagram.defaultInstagramAccount
      ? instagram.defaultInstagramAccount.username
      : '';

    const totalNumItems = atMentionedItems.length + nonAtMentionedItems.length;
    const numSelectedItems = Object.keys(selectedItems).length;

    const requestRightsButton = isRequestingRights ? (
      <RequestRightsLabel>
        Request Rights For {numSelectedItems}...
        <RequestingRightsSpinner />
      </RequestRightsLabel>
    ) : (
      `Request Rights For ${numSelectedItems} ${
        numSelectedItems === 1 ? 'Item' : 'Items'
      }`
    );

    const autoRequestRightsButton = !hasUIARequestRightsPermission
      ? 'Error'
      : nonAtMentionedItems.length > 0
        ? 'Next'
        : 'Done';

    return (
      isAutomated !== null && (
        <FullscreenModal
          isActive={true}
          headerTitle={'Request Rights'}
          isOpen={isOpen}
          onClose={closeModal}
          trackingId={GalleriesTrackingIds.REQUEST_RIGHTS_AT_MENTIONED}
        >
          <WorkflowBreadcrumbs
            sectionTitles={['add to galleries', 'request rights']}
            currentIndex={1}
            className='cu-mt-5'
          />
          <div className='cu-d-flex cu-mt-5 cu-w-100'>
            <LeftColumn>
              <div className='cu-mb-3'>
                <span className='cu-text-subsection-2'>
                  @{brandName} mentions{' '}
                </span>
                <span className='cu-text-body-secondary'>
                  {atMentionedItems.length}{' '}
                  {atMentionedItems.length === 1 ? 'Item' : 'Items'}
                </span>
              </div>
              <MediaGrid>
                {this.convertContentItemToSelectable(atMentionedItems)}
              </MediaGrid>
              {nonAtMentionedItems.length !== 0 && (
                <>
                  <div className='cu-mt-7 cu-mb-3'>
                    <span className='cu-text-subsection-2 cu-disabled'>
                      non @mentions{' '}
                    </span>
                    <span className='cu-text-body-secondary'>
                      {nonAtMentionedItems.length}{' '}
                      {nonAtMentionedItems.length === 1 ? 'Item' : 'Items'}
                    </span>
                  </div>
                  <MediaGrid>
                    {this.convertContentItemToSelectable(
                      nonAtMentionedItems,
                      true,
                    )}
                  </MediaGrid>
                </>
              )}
            </LeftColumn>
            {!isAutomated ? (
              <RightColumn>
                <div className='cu-text-subtitle cu-mb-3'>
                  Choose a Messaging Campaign
                </div>
                <div className='cu-mb-2'>
                  Choose a messaging campaign to use. We're able to
                  automatically post messages requesting rights on{' '}
                  {atMentionedItems.length} of the {totalNumItems} selected
                  items as they @mention your brand.
                </div>
                {nonAtMentionedItems.length !== 0 && (
                  <div className='cu-mb-4'>
                    Next, we'll help you request rights on the remaining{' '}
                    {nonAtMentionedItems.length} items
                    {hasChromeExtension &&
                      ' using the Bazaarvoice Chrome Extension'}
                    .
                  </div>
                )}
                <div className='cu-mb-1'>Messaging Campaign</div>
                <RotatingRightsMessages selectedItem={atMentionedItems[0]} />
                <div className='cu-d-flex cu-justify-content-end cu-mt-2'>
                  <button
                    data-testid='at-mentioned-modal-skip-rights-button'
                    className='cu-button-flat'
                    onClick={openRightsSkippedModal}
                  >
                    Request Rights Later
                  </button>
                  <SmallTooltip
                    isDisabled={hasUIARequestRightsPermission}
                    target={
                      <RequestRightsButton
                        onClick={this.checkNumSelectedAndRequestRights}
                        isRequestingRights={isRequestingRights}
                        isDisabled={!hasUIARequestRightsPermission}
                      >
                        {requestRightsButton}
                      </RequestRightsButton>
                    }
                    content={
                      <div>
                        {`You don't have permission to request rights. Contact a group admin if
                    this is a mistake.`}
                      </div>
                    }
                  />
                </div>
                <div className='cu-mt-2'>
                  {this.state.showNoSelectedItemsAlert && (
                    <Alert type='warning'>
                      You don’t have any content selected that you need to
                      request rights for.
                    </Alert>
                  )}
                </div>
              </RightColumn>
            ) : (
              <RightColumn>
                <div className='cu-mt-5'>
                  <Alert type='info'>
                    The Rights Requests will be automatically sent when the
                    content(s) are added to Galleries
                  </Alert>
                  <div className='cu-d-flex cu-justify-content-end cu-mt-2'>
                    <SmallTooltip
                      isDisabled={hasUIARequestRightsPermission}
                      target={
                        <RequestRightsButton
                          onClick={this.autoRightsRequestButtonHandler}
                          isRequestingRights={isRequestingRights}
                          isDisabled={!hasUIARequestRightsPermission}
                        >
                          {autoRequestRightsButton}
                        </RequestRightsButton>
                      }
                      content={
                        <div>
                          {`You don't have permission to request rights. Contact a group admin if
                    this is a mistake.`}
                        </div>
                      }
                    />
                  </div>
                </div>
              </RightColumn>
            )}
          </div>
          <RightsSkippedModal onConfirm={this.closeAndOpenNextModal} />
        </FullscreenModal>
      )
    );
  }

  private readonly checkNumSelectedAndRequestRights = () => {
    const { selectedItems } = this.props;
    const numSelectedItems = Object.keys(selectedItems).length;

    if (numSelectedItems === 0) {
      this.setState({ showNoSelectedItemsAlert: true });
    } else {
      this.requestRightsForAtMentioned();
    }
  };

  private readonly convertContentItemToSelectable = (
    contentItems: IContentLibraryItem[],
    isDisabled: boolean = false,
  ) => {
    const { selectedItems, itemsPendingApproval } = this.props;

    const imgSize =
      contentItems.length === 1
        ? BULK_RIGHTS_WORKFLOW_LARGE_MEDIA_WIDTH
        : BULK_RIGHTS_WORKFLOW_SMALL_MEDIA_WIDTH;

    return contentItems.map((contentItem, index) => {
      const darkGradientAndInfoOverlay = (
        <>
          <DarkGradientOverlay />
          <InfoOverlay contentItem={contentItem} />
        </>
      );

      const pendingProps = {
        overlay: <PendingIconOverlay />,
        handleClick: () => undefined,
      };

      const isSelected = PojoMap.has(selectedItems, contentItem.id);
      const selectableItemProps = {
        isSelected,
        showBorder: isSelected,
        showSelectableOverlay: true,
        handleClick: () => this.handleImageSelect(contentItem),
        hoverOverlay: darkGradientAndInfoOverlay,
      };

      const disabledProps = {
        showBorder: false,
        showSelectableOverlay: false,
        isDisabled: true,
        handleClick: () => undefined,
      };

      const itemProps = (() => {
        if (isDisabled) {
          return disabledProps;
        } else if (PojoSet.has(itemsPendingApproval, contentItem.id)) {
          return pendingProps;
        } else {
          return selectableItemProps;
        }
      })();

      return (
        <SelectableImage
          key={index}
          imgSrc={getContentItemImageUrl(contentItem, imgSize)}
          {...itemProps}
        />
      );
    });
  };

  private readonly handleImageSelect = (selectedItem: IContentLibraryItem) => {
    const {
      actions: {
        modal: { selectImage, deselectImage },
      },
      selectedItems,
    } = this.props;

    const itemId = selectedItem.id;
    if (PojoMap.has(selectedItems, itemId)) {
      deselectImage(selectedItem);
    } else {
      selectImage(selectedItem);
      this.setState({ showNoSelectedItemsAlert: false });
    }
  };

  private readonly requestRightsForAtMentioned = () => {
    const {
      selectedItems,
      requestRights: { configurations, selectedConfigurationId },
      actions: {
        requestRights: {
          graphRightsRequestStarted,
          graphRightsRequestSucceeded,
          graphRightsRequestFailed,
        },
        modal: { setItemPendingApproval },
        bulkActions: { clearSelectedItems },
      },
      isIncludeUsernameEnabled,
    } = this.props;

    const partialCuidsAndItems = PojoMap.values(selectedItems).map(
      (selectedItem) => ({
        partialCuid: PartialCuid.build(selectedItem),
        item: selectedItem,
      }),
    );
    const selectedRightsRequests = Normalized.getById(
      configurations,
      selectedConfigurationId,
    ).rightsRequestData.data;
    clearSelectedItems();

    graphRightsRequestStarted();
    const rightsRequestPromises = partialCuidsAndItems.map(
      ({ partialCuid, item }, index) => {
        const rightsRequest =
          selectedRightsRequests[index % selectedRightsRequests.length];

        const personalizedMessage = personalizeRightsRequestMessage(
          isIncludeUsernameEnabled,
          rightsRequest.message,
          item.socialDetails?.user?.username ?? '',
        );

        const request = requestMultiHandleRightsGraphApi({
          partialCuid,
          params: {
            approvalPhrase: rightsRequest?.approvalPhrase,
            requestForRightsMessage: personalizedMessage,
          },
        });

        request.then(() => setItemPendingApproval(item.id));
      },
    );

    Promise.all(rightsRequestPromises)
      .then(graphRightsRequestSucceeded)
      .catch(graphRightsRequestFailed)
      .finally(this.closeAndOpenNextModal);
  };

  private readonly autoRightsRequestButtonHandler = () => {
    const {
      selectedItems,
      actions: {
        bulkActions: { clearSelectedItems },
      },
    } = this.props;
    clearSelectedItems();
    this.closeAndOpenNextModal();
  };

  private readonly closeAndOpenNextModal = () => {
    const {
      nonAtMentionedItems,
      selectedItems,
      actions: {
        modal: { closeModal },
        requestRightsNonMentionedModal: { openModal },
        bulkActions: { updateItemOverlayDetails, clearSelectedItems },
      },
    } = this.props;

    const partialCuids = PojoMap.values(selectedItems).map(PartialCuid.build);
    partialCuids.forEach((partialCuid) =>
      updateItemOverlayDetails(partialCuid, true),
    );

    if (nonAtMentionedItems.length > 0) {
      openModal(nonAtMentionedItems);
    } else {
      clearSelectedItems();
    }

    closeModal();
  };
}
// Subtract the top margin + top bar height from the max viewport to get max height of left panel
// Need to set this in order to make the panel scrollable
const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 60%;
`;

const RightColumn = styled.div`
  margin-left: ${({ theme }) => theme.spacings[5]};
  width: 40%;
`;

const RequestingRightsSpinner = styled(InlineSpinner)`
  && {
    width: 1.1em;
    height: 1.1em;
    margin: 0 0 0 ${({ theme }) => theme.spacings[1]};

    circle {
      stroke: ${({ theme }) => theme.colors.white};
    }

    circle.cu-spinner-spiral {
      stroke: ${({ theme }) => theme.colors.gray3};
    }
  }
`;

const RequestRightsLabel = styled.span`
  display: inline-block;
  vertical-align: middle;
`;

interface IRequestRightsButton {
  isRequestingRights: boolean;
}
const RequestRightsButton = styled(PrimaryButton)<IRequestRightsButton>`
  ${({ isRequestingRights, theme }) =>
    isRequestingRights
      ? `&& {
      background-color: ${theme.colors.aliases.primary};
    }`
      : ''}
  margin-left: ${({ theme }) => theme.spacings[2]};
`;

const ConnectedAtMentionedModal = connect(
  mapStateToProps,
  mapDispatchToProps,
)(RequestRightsAtMentioned);

export default ConnectedAtMentionedModal;

angular.module('curalate').component('requestRightsAtMentioned', {
  controller: makeAngularReactController(<ConnectedAtMentionedModal />),
});
